import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Train, Plus, ChevronLeft, Loader2, Wallet,
  RotateCcw, KeyRound, Trash2, Activity, Search, IndianRupee, Download,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const RS = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const emptyKyc = {
  state: '', name: '', uname: '', email: '', mobileno: '',
  apno: '', dateofbirth: '', doorno: '', block: '', streetlo: '',
  cityname: '', village: '', district: '', pincode: '',
  idproof_no: '', id_type: 'aadhar', Id_Photo: '', partnerid: '',
};

export default function PartnerRailtel() {
  const navigate = useNavigate();
  const basePath = window.location.pathname.startsWith('/partner') ? '/partner' : '/portal';
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('subscribers');
  const [packages, setPackages] = useState([]);
  const [subs, setSubs] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [transactions, setTransactions] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [isLco, setIsLco] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [createPkgId, setCreatePkgId] = useState(''); // either 'pkg:<id>' or 'combo:<id>'
  const [createKyc, setCreateKyc] = useState(emptyKyc);
  const [createBusy, setCreateBusy] = useState(false);
  const [createPartialResult, setCreatePartialResult] = useState(null); // {recharge_id, iptv_status, iptv_message}
  const [createIptvRetryBusy, setCreateIptvRetryBusy] = useState(false);

  const [showRecharge, setShowRecharge] = useState(null);  // {sub}
  const [rechargePkgId, setRechargePkgId] = useState('');
  const [rechargeBusy, setRechargeBusy] = useState(false);
  const [rechargeError, setRechargeError] = useState(null);
  const [rechargeAttempts, setRechargeAttempts] = useState(0);

  const [showStatus, setShowStatus] = useState(null);  // {sub}
  const [statusInfo, setStatusInfo] = useState(null);

  const [showReset, setShowReset] = useState(null);
  const [newPwd, setNewPwd] = useState('');

  const [showTopup, setShowTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState(500);

  // Import existing subscriber
  const [showImport, setShowImport] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importForm, setImportForm] = useState({
    uname: '', stcode: 'KA', package_id: '', name: '', mobileno: '', email: '',
  });

  // Initial load
  useEffect(() => {
    if (!token) { navigate(basePath); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/api/portal/my-services`, { headers });
        if (r.status === 401) { navigate(basePath); return; }
        const data = await r.json();
        const customers = data.customers || [];
        setAllCustomers(customers);
        const storedActive = localStorage.getItem('portal_active_customer_id');
        const partnerCust = customers.find(c => c.customer_type === 'partner');
        const lcoCust = customers.find(c => c.parent_partner_id);
        const active = (storedActive && customers.find(c => c.customer_id === storedActive)) || partnerCust || lcoCust || customers[0];
        if (!active) { setLoading(false); return; }
        setCustomerId(active.customer_id);
        setIsLco(!!active.parent_partner_id);
      } catch { setLoading(false); }
    })();
  }, []); // eslint-disable-line

  // Load main data once customerId resolved
  const loadAll = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [pkgRes, subsRes, walletRes, txnRes] = await Promise.all([
        fetch(`${API}/api/portal/railtel/packages`, { headers }),
        fetch(`${API}/api/portal/railtel/subscribers`, { headers }),
        fetch(`${API}/api/railtel/portal/balance?customer_id=${customerId}`, { headers }),
        fetch(`${API}/api/railtel/portal/transactions?customer_id=${customerId}&limit=20`, { headers }),
      ]);
      if (pkgRes.ok) setPackages((await pkgRes.json()).packages || []);
      if (subsRes.ok) setSubs((await subsRes.json()).subscribers || []);
      if (walletRes.ok) setWallet(await walletRes.json());
      if (txnRes.ok) setTransactions((await txnRes.json()).transactions || []);
    } catch { toast.error('Failed to load Railtel data'); }
    setLoading(false);
  }, [customerId]); // eslint-disable-line

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleCreate = async () => {
    if (!createPkgId) { toast.error('Pick a package or combo'); return; }
    const required = ['state', 'name', 'uname', 'email', 'mobileno', 'dateofbirth', 'cityname', 'district', 'pincode', 'idproof_no'];
    for (const k of required) {
      if (!createKyc[k]) { toast.error(`Missing field: ${k}`); return; }
    }
    setCreateBusy(true);
    setCreatePartialResult(null);
    const isCombo = createPkgId.startsWith('combo:');
    const id = createPkgId.replace(/^(pkg|combo):/, '');
    try {
      const r = await fetch(`${API}/api/portal/railtel/subscriber/create`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(isCombo ? { combo_id: id } : { package_id: id }),
          stcode: createKyc.state.length === 2 ? createKyc.state : 'KA',
          kyc: createKyc,
        }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(`Subscriber created (charged ${RS(d.wallet_charged)})`);
        setShowCreate(false); setCreateKyc(emptyKyc); setCreatePkgId('');
        loadAll();
      } else if (r.ok && d.from_combo && d.iptv_status === 'failed') {
        // Combo create succeeded for Railtel leg but IPTV leg failed — show retry
        setCreatePartialResult(d);
        toast.warning('Subscriber created — IPTV leg failed. Retry below.', { duration: 8000 });
        loadAll();
      } else {
        const msg = d.detail || d.message || 'Failed';
        // Detect provider-side restrictions and give actionable guidance
        if (typeof msg === 'string' && /cannot be created through the API|api.*disabled|api.*restricted/i.test(msg)) {
          toast.error(
            `Provider says: "${msg}"\n\nThis is a Railtel-side restriction. Please contact Railtel/Railwire support to re-enable API KYC creation for partner ID 6219361247.`,
            { duration: 12000 }
          );
        } else {
          toast.error(msg, { duration: 6000 });
        }
      }
    } catch { toast.error('Network error'); }
    setCreateBusy(false);
  };

  const handleCreateIptvRetry = async () => {
    if (!createPartialResult?.recharge_id) return;
    setCreateIptvRetryBusy(true);
    try {
      const r = await fetch(`${API}/api/portal/railtel/combo/retry-leg`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recharge_id: createPartialResult.recharge_id, leg: 'iptv' }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success('IPTV leg succeeded on retry');
        setShowCreate(false); setCreateKyc(emptyKyc); setCreatePkgId(''); setCreatePartialResult(null);
        loadAll();
      } else {
        toast.error(d.message || d.detail || 'IPTV retry failed', { duration: 6000 });
        setCreatePartialResult(prev => ({ ...prev, iptv_message: d.message || d.detail || prev?.iptv_message }));
      }
    } catch { toast.error('Network error'); }
    setCreateIptvRetryBusy(false);
  };

  const handleRecharge = async () => {
    if (!rechargePkgId || !showRecharge) return;
    setRechargeBusy(true);
    setRechargeError(null);
    try {
      const r = await fetch(`${API}/api/portal/railtel/subscriber/recharge`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: showRecharge.uname,
          package_id: rechargePkgId,
          stcode: showRecharge.stcode,
        }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(`Recharge successful — valid till ${d.servicedate || '—'}`);
        setShowRecharge(null); setRechargePkgId(''); setRechargeAttempts(0);
        loadAll();
      } else {
        const msg = d.detail || 'Recharge failed';
        setRechargeError(msg);
        setRechargeAttempts(prev => prev + 1);
        toast.error(msg, { duration: 6000 });
      }
    } catch (e) {
      const msg = 'Network error — please try again';
      setRechargeError(msg);
      setRechargeAttempts(prev => prev + 1);
      toast.error(msg);
    }
    setRechargeBusy(false);
  };

  const handleDeactivate = async (sub) => {
    if (!window.confirm(`Deactivate ${sub.uname}?`)) return;
    try {
      const r = await fetch(`${API}/api/portal/railtel/subscriber/deactivate`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: sub.uname, stcode: sub.stcode }),
      });
      const d = await r.json();
      if (r.ok && d.success) { toast.success('Deactivated'); loadAll(); }
      else toast.error(d.detail || 'Failed');
    } catch { toast.error('Network error'); }
  };

  const handleCheckStatus = async (sub) => {
    setShowStatus({ sub });
    setStatusInfo(null);
    try {
      const r = await fetch(`${API}/api/portal/railtel/subscriber/${sub.uname}/status?stcode=${sub.stcode}`, { headers });
      if (r.ok) setStatusInfo(await r.json());
      else { const d = await r.json(); toast.error(d.detail || 'Failed'); setShowStatus(null); }
    } catch { toast.error('Network error'); setShowStatus(null); }
  };

  const handleReset = async () => {
    if (!showReset || !newPwd || newPwd.length < 6) { toast.error('Password ≥ 6 chars'); return; }
    try {
      const r = await fetch(`${API}/api/portal/railtel/subscriber/reset-password`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: showReset.uname, stcode: showReset.stcode, new_password: newPwd }),
      });
      const d = await r.json();
      if (r.ok && d.success) { toast.success('Password reset'); setShowReset(null); setNewPwd(''); }
      else toast.error(d.detail || 'Failed');
    } catch { toast.error('Network error'); }
  };

  const handleTopup = async () => {
    if (!topupAmount || topupAmount < 1) { toast.error('Enter amount'); return; }
    try {
      const r = await fetch(`${API}/api/railtel/portal/wallet/topup/initiate`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId, amount: parseFloat(topupAmount) }),
      });
      const d = await r.json();
      if (r.ok && d.success && d.payment_url) {
        window.location.href = d.payment_url;
      } else toast.error(d.detail || 'Failed to initiate top-up');
    } catch { toast.error('Network error'); }
  };

  const handleImport = async () => {
    if (!importForm.uname.trim()) { toast.error('Enter Railtel username'); return; }
    if (!importForm.stcode.trim()) { toast.error('Enter state code'); return; }
    setImportBusy(true);
    try {
      const r = await fetch(`${API}/api/portal/railtel/subscriber/import`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uname: importForm.uname.trim(),
          stcode: importForm.stcode.trim().toUpperCase(),
          package_id: importForm.package_id || null,
          name: importForm.name.trim(),
          mobileno: importForm.mobileno.trim(),
          email: importForm.email.trim(),
        }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(`Imported ${d.subscriber.uname} (${d.subscriber.status})`);
        setShowImport(false);
        setImportForm({ uname: '', stcode: 'KA', package_id: '', name: '', mobileno: '', email: '' });
        loadAll();
      } else {
        toast.error(d.detail || 'Import failed', { duration: 6000 });
      }
    } catch { toast.error('Network error'); }
    setImportBusy(false);
  };

  const subscribersToShow = subs;

  // ---- Combo state ----
  const [combos, setCombos] = useState([]);
  const [showCombo, setShowCombo] = useState(null); // {sub} when opened from Subscribers tab
  const [comboId, setComboId] = useState('');
  const [comboBusy, setComboBusy] = useState(false);
  const [comboResult, setComboResult] = useState(null); // {recharge_id, railtel_status, iptv_status, ...}
  const [comboRetryBusy, setComboRetryBusy] = useState({ railtel: false, iptv: false });

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/portal/railtel/combos`, { headers });
        if (r.ok) setCombos((await r.json()).combos || []);
      } catch { /* ignore */ }
    })();
  }, [customerId]); // eslint-disable-line

  const openComboDialog = (sub) => {
    setShowCombo({ sub });
    setComboId(''); setComboResult(null);
    setComboRetryBusy({ railtel: false, iptv: false });
  };

  const handleComboRecharge = async () => {
    if (!comboId || !showCombo) { toast.error('Pick a combo'); return; }
    setComboBusy(true); setComboResult(null);
    try {
      const r = await fetch(`${API}/api/portal/railtel/combo/recharge`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          combo_id: comboId,
          uname: showCombo.sub.uname,
          stcode: showCombo.sub.stcode,
          mobile_no: showCombo.sub.mobileno,
          subscriber_name: showCombo.sub.name,
          state: 'Karnataka',
          city: 'Bangalore',
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setComboResult(d);
        if (d.success) {
          toast.success('Combo recharge successful — both legs OK');
          loadAll();
        } else {
          toast.warning('Combo: partial success — see retry options below', { duration: 8000 });
        }
      } else {
        toast.error(d.detail || 'Combo recharge failed', { duration: 6000 });
      }
    } catch { toast.error('Network error'); }
    setComboBusy(false);
  };

  const handleComboRetry = async (leg) => {
    if (!comboResult?.recharge_id) return;
    setComboRetryBusy(prev => ({ ...prev, [leg]: true }));
    try {
      const r = await fetch(`${API}/api/portal/railtel/combo/retry-leg`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recharge_id: comboResult.recharge_id, leg }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(`${leg.toUpperCase()} leg succeeded on retry`);
        setComboResult(prev => ({ ...prev, [`${leg}_status`]: 'success', [`${leg}_message`]: d.message || '' }));
        loadAll();
      } else {
        toast.error(d.message || d.detail || `${leg} retry failed`, { duration: 6000 });
        setComboResult(prev => ({ ...prev, [`${leg}_message`]: d.message || d.detail || '' }));
      }
    } catch { toast.error('Network error'); }
    setComboRetryBusy(prev => ({ ...prev, [leg]: false }));
  };

  if (loading && !packages.length) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
    </div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-railtel-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`${basePath}/dashboard`)} className="p-1 hover:bg-slate-100 rounded">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <Train className="h-5 w-5 text-orange-600" />
          <div className="flex-1">
            <h1 className="font-bold text-lg flex items-center gap-2">ANT+RAILTEL</h1>
            <p className="text-xs text-slate-500">{isLco ? 'LCO portal' : 'Partner portal'}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-4">
        {/* Wallet card */}
        <Card className="bg-gradient-to-br from-orange-500 to-amber-600 text-white">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-xs flex items-center gap-1"><Wallet className="h-3 w-3" /> ANT+RAILTEL Wallet</p>
              <p className="text-3xl font-bold mt-1">{RS(wallet.balance)}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setShowTopup(true)} data-testid="railtel-topup-btn">
              <Plus className="h-4 w-4 mr-1" /> Top Up
            </Button>
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="subscribers" data-testid="railtel-tab-subs">Subscribers ({subs.length})</TabsTrigger>
            <TabsTrigger value="packages" data-testid="railtel-tab-pkgs">Packages ({packages.length})</TabsTrigger>
            <TabsTrigger value="combos" data-testid="railtel-tab-combos">Combos ({combos.length})</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="railtel-tab-txns">Transactions</TabsTrigger>
          </TabsList>

          <TabsContent value="subscribers" className="space-y-3">
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowImport(true)} data-testid="railtel-import-sub-btn">
                <Download className="h-4 w-4 mr-1" /> Import Existing
              </Button>
              <Button onClick={() => { setShowCreate(true); setCreateKyc(emptyKyc); setCreatePkgId(''); setCreatePartialResult(null); }} data-testid="railtel-new-sub-btn">
                <Plus className="h-4 w-4 mr-1" /> New Subscriber
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                {subscribersToShow.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <Train className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                    <p>No Railtel subscribers yet</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {subscribersToShow.map(s => (
                      <div key={s.subscriber_id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`railtel-sub-${s.uname}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{s.uname}</p>
                            <Badge variant="outline">{s.stcode}</Badge>
                            <Badge className={s.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                              {s.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{s.name} • {s.mobileno}</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {s.package_name} • last recharge: {s.last_servicedate || s.last_recharge_at?.split('T')[0] || '—'}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <Button size="sm" variant="outline" onClick={() => { setShowRecharge({ sub: s, ...s }); setRechargePkgId(s.package_id || ''); setRechargeError(null); setRechargeAttempts(0); }} data-testid={`railtel-recharge-${s.uname}`}>
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Recharge
                          </Button>
                          {combos.length > 0 && (
                            <Button size="sm" variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50" onClick={() => openComboDialog(s)} data-testid={`railtel-combo-${s.uname}`}>
                              <Plus className="h-3.5 w-3.5 mr-1" /> Combo
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => handleCheckStatus(s)}>
                            <Activity className="h-3.5 w-3.5 mr-1" /> Status
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setShowReset(s); setNewPwd(''); }}>
                            <KeyRound className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeactivate(s)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="packages">
            <Card>
              <CardContent className="p-0">
                {packages.length === 0 ? (
                  <div className="p-12 text-center text-slate-500"><p>No packages assigned to you yet. Contact admin.</p></div>
                ) : (
                  <div className="divide-y">
                    {packages.map(p => (
                      <div key={p.package_id} className="p-4 flex items-center gap-3" data-testid={`railtel-pkg-${p.package_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{p.name}</p>
                          <p className="text-xs text-slate-500">
                            Railtel #{p.railtel_package_id} • Validity {p.validity_days}d
                            {p.price_source === 'lco_price_from_partner' ? ' • Partner-set price' : ''}
                            {p.price_source === 'parent_partner_price_fallback' ? ' • Awaiting partner LCO price' : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Your Price</p>
                          <p className="text-lg font-bold text-orange-600">{RS(p.effective_price)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="combos">
            <Card>
              <CardContent className="p-0">
                {combos.length === 0 ? (
                  <div className="p-12 text-center text-slate-500"><p>No combos available yet. Contact admin.</p></div>
                ) : (
                  <div className="divide-y">
                    {combos.map(c => (
                      <div key={c.combo_id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`railtel-combo-row-${c.combo_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{c.name}</p>
                            <Badge className="bg-purple-100 text-purple-700">ANT+RAILTEL</Badge>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Railtel: {c.railtel_package_name} • IPTV: {c.iptv_package_name} • Validity {c.validity_days}d
                          </p>
                          {c.price_source === 'parent_partner_price_fallback' && (
                            <p className="text-xs text-amber-600 mt-0.5">LCO price not set — awaiting partner</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Your Price</p>
                          <p className="text-lg font-bold text-purple-600">{RS(c.effective_price)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions">
            <Card>
              <CardContent className="p-0">
                {transactions.length === 0 ? (
                  <div className="p-12 text-center text-slate-500"><p>No transactions yet</p></div>
                ) : (
                  <div className="divide-y">
                    {transactions.map(t => (
                      <div key={t.transaction_id} className="p-3 flex items-center justify-between text-sm">
                        <div className="min-w-0 flex-1">
                          <p className="text-slate-700 truncate">{t.description}</p>
                          <p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString('en-IN')}</p>
                        </div>
                        <p className={`font-bold ${t.type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {t.type === 'credit' ? '+' : '−'}{RS(t.amount)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Subscriber Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="railtel-create-dialog">
          <DialogHeader>
            <DialogTitle>New Railtel Subscriber</DialogTitle>
            <DialogDescription>KYC details + package — wallet will be debited on success</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Package or Combo *</Label>
              <Select value={createPkgId} onValueChange={setCreatePkgId}>
                <SelectTrigger data-testid="railtel-create-pkg-select"><SelectValue placeholder="Pick a package or combo" /></SelectTrigger>
                <SelectContent>
                  {packages.length > 0 && combos.filter(c => c.is_active && c.price_source !== 'parent_partner_price_fallback').length > 0 && (
                    <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Single Packages</div>
                  )}
                  {packages.map(p => (
                    <SelectItem key={`pkg:${p.package_id}`} value={`pkg:${p.package_id}`}>
                      {p.name} — {RS(p.effective_price)} ({p.validity_days}d)
                    </SelectItem>
                  ))}
                  {packages.length > 0 && combos.filter(c => c.is_active && c.price_source !== 'parent_partner_price_fallback').length > 0 && (
                    <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-purple-500 border-t mt-1">ANT+RAILTEL Combos</div>
                  )}
                  {combos.filter(c => c.is_active && c.price_source !== 'parent_partner_price_fallback').map(c => (
                    <SelectItem key={`combo:${c.combo_id}`} value={`combo:${c.combo_id}`}>
                      🔗 {c.name} — {RS(c.effective_price)} ({c.validity_days}d, +IPTV)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {createPkgId.startsWith('combo:') && (
                <p className="text-xs text-purple-600 mt-1">
                  This combo will create the Railtel subscriber AND activate IPTV in one step. Single wallet debit. If IPTV leg fails, you can retry without re-charging.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full Name *</Label><Input value={createKyc.name} onChange={e => setCreateKyc({ ...createKyc, name: e.target.value })} /></div>
              <div><Label>Username (uname) *</Label><Input value={createKyc.uname} onChange={e => setCreateKyc({ ...createKyc, uname: e.target.value })} /></div>
              <div><Label>Email *</Label><Input type="email" value={createKyc.email} onChange={e => setCreateKyc({ ...createKyc, email: e.target.value })} /></div>
              <div><Label>Mobile *</Label><Input value={createKyc.mobileno} onChange={e => setCreateKyc({ ...createKyc, mobileno: e.target.value })} /></div>
              <div><Label>State Code (2-letter) *</Label><Input value={createKyc.state} maxLength={20} onChange={e => setCreateKyc({ ...createKyc, state: e.target.value })} placeholder="Karnataka" /></div>
              <div><Label>DOB (YYYY-MM-DD) *</Label><Input type="date" value={createKyc.dateofbirth} onChange={e => setCreateKyc({ ...createKyc, dateofbirth: e.target.value })} /></div>
              <div><Label>Aadhar / ID No *</Label><Input value={createKyc.idproof_no} onChange={e => setCreateKyc({ ...createKyc, idproof_no: e.target.value })} /></div>
              <div><Label>City *</Label><Input value={createKyc.cityname} onChange={e => setCreateKyc({ ...createKyc, cityname: e.target.value })} /></div>
              <div><Label>District *</Label><Input value={createKyc.district} onChange={e => setCreateKyc({ ...createKyc, district: e.target.value })} /></div>
              <div><Label>Pincode *</Label><Input value={createKyc.pincode} onChange={e => setCreateKyc({ ...createKyc, pincode: e.target.value })} /></div>
              <div><Label>Door No</Label><Input value={createKyc.doorno} onChange={e => setCreateKyc({ ...createKyc, doorno: e.target.value })} /></div>
              <div><Label>Application No (apno)</Label><Input value={createKyc.apno} onChange={e => setCreateKyc({ ...createKyc, apno: e.target.value })} /></div>
            </div>
            <div><Label>Address Line</Label><Input value={createKyc.streetlo} onChange={e => setCreateKyc({ ...createKyc, streetlo: e.target.value })} /></div>

            {createPartialResult && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="create-iptv-retry-panel">
                <p className="font-semibold text-amber-800">Partial success — IPTV leg failed</p>
                <p className="text-xs text-amber-700">Subscriber was created at Railtel and your wallet was charged. Click Retry to attempt the IPTV activation again — no additional debit.</p>
                {createPartialResult.iptv_message && (
                  <p className="text-xs text-red-600 break-words">Provider: {createPartialResult.iptv_message}</p>
                )}
                <Button size="sm" onClick={handleCreateIptvRetry} disabled={createIptvRetryBusy} className="bg-amber-600 hover:bg-amber-700" data-testid="create-iptv-retry-btn">
                  {createIptvRetryBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                  Retry IPTV Activation
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setCreatePartialResult(null); }}>
              {createPartialResult ? 'Close' : 'Cancel'}
            </Button>
            {!createPartialResult && (
              <Button onClick={handleCreate} disabled={createBusy} data-testid="railtel-create-save">
                {createBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Create
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recharge Dialog */}
      <Dialog open={!!showRecharge} onOpenChange={v => { if (!v) { setShowRecharge(null); setRechargeError(null); setRechargeAttempts(0); } }}>
        <DialogContent data-testid="railtel-recharge-dialog">
          <DialogHeader>
            <DialogTitle>Recharge {showRecharge?.uname}</DialogTitle>
            <DialogDescription>State: {showRecharge?.stcode}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Package *</Label>
            <Select value={rechargePkgId} onValueChange={setRechargePkgId}>
              <SelectTrigger><SelectValue placeholder="Pick a package" /></SelectTrigger>
              <SelectContent>
                {packages.map(p => (
                  <SelectItem key={p.package_id} value={p.package_id}>
                    {p.name} — {RS(p.effective_price)} ({p.validity_days}d)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {rechargeError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm space-y-1" data-testid="railtel-recharge-error">
                <p className="font-semibold text-red-700">Recharge failed{rechargeAttempts > 1 ? ` (attempt ${rechargeAttempts})` : ''}</p>
                <p className="text-red-600 break-words">{rechargeError}</p>
                <p className="text-xs text-red-500/80 pt-1">
                  You can change the package above and click <strong>Retry</strong>, or close this dialog.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRecharge(null); setRechargeError(null); setRechargeAttempts(0); }}>Cancel</Button>
            <Button onClick={handleRecharge} disabled={rechargeBusy} data-testid="railtel-recharge-save"
                    className={rechargeError ? 'bg-amber-600 hover:bg-amber-700' : ''}>
              {rechargeBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
              {rechargeError ? 'Retry Recharge' : 'Recharge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={!!showStatus} onOpenChange={v => { if (!v) { setShowStatus(null); setStatusInfo(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Status — {showStatus?.sub?.uname}</DialogTitle></DialogHeader>
          {!statusInfo ? <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Online</span><Badge>{statusInfo.online_status}</Badge></div>
              <div className="flex justify-between"><span className="text-slate-500">Subscription</span><Badge>{statusInfo.subscription_status}</Badge></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Dialog */}
      <Dialog open={!!showReset} onOpenChange={v => { if (!v) setShowReset(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password — {showReset?.uname}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>New Password *</Label>
            <Input value={newPwd} onChange={e => setNewPwd(e.target.value)} type="text" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReset(null)}>Cancel</Button>
            <Button onClick={handleReset}>Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Existing Subscriber Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-lg" data-testid="railtel-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Existing Railtel Subscriber</DialogTitle>
            <DialogDescription>
              Map an existing Railtel/Railwire username (from the old portal) to your account. The system will verify it at the provider before importing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Railtel Username *</Label>
                <Input
                  data-testid="railtel-import-uname"
                  value={importForm.uname}
                  onChange={e => setImportForm({ ...importForm, uname: e.target.value })}
                  placeholder="e.g. RAIL_user123"
                />
              </div>
              <div>
                <Label>State Code *</Label>
                <Input
                  data-testid="railtel-import-stcode"
                  value={importForm.stcode}
                  onChange={e => setImportForm({ ...importForm, stcode: e.target.value.toUpperCase() })}
                  maxLength={2}
                  placeholder="KA"
                />
              </div>
            </div>
            <div>
              <Label>Default Recharge Package <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
              <Select value={importForm.package_id} onValueChange={v => setImportForm({ ...importForm, package_id: v })}>
                <SelectTrigger data-testid="railtel-import-pkg"><SelectValue placeholder="— pick a package —" /></SelectTrigger>
                <SelectContent>
                  {packages.map(p => (
                    <SelectItem key={p.package_id} value={p.package_id}>
                      {p.name} ({RS(p.effective_price)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Used as the default for one-click recharge later.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Subscriber Name</Label>
                <Input
                  data-testid="railtel-import-name"
                  value={importForm.name}
                  onChange={e => setImportForm({ ...importForm, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Mobile No</Label>
                <Input
                  data-testid="railtel-import-mobile"
                  value={importForm.mobileno}
                  onChange={e => setImportForm({ ...importForm, mobileno: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="railtel-import-email"
                value={importForm.email}
                onChange={e => setImportForm({ ...importForm, email: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)} disabled={importBusy}>Cancel</Button>
            <Button onClick={handleImport} disabled={importBusy} data-testid="railtel-import-confirm">
              {importBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              Verify & Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Combo Recharge Dialog (with per-leg retry) */}
      <Dialog open={!!showCombo} onOpenChange={v => { if (!v) { setShowCombo(null); setComboResult(null); setComboId(''); } }}>
        <DialogContent className="max-w-lg" data-testid="railtel-combo-dialog">
          <DialogHeader>
            <DialogTitle>Combo Recharge — {showCombo?.sub?.uname}</DialogTitle>
            <DialogDescription>
              Charges Railtel wallet once. Calls Railtel + IPTV providers sequentially. On partial failure, retry the failed leg below — no refund.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Pick a Combo *</Label>
              <Select value={comboId} onValueChange={setComboId} disabled={!!comboResult}>
                <SelectTrigger data-testid="combo-recharge-pick"><SelectValue placeholder="— Select combo —" /></SelectTrigger>
                <SelectContent>
                  {combos.filter(c => c.is_active && c.price_source !== 'parent_partner_price_fallback').map(c => (
                    <SelectItem key={c.combo_id} value={c.combo_id}>
                      {c.name} — {RS(c.effective_price)} ({c.validity_days}d)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {comboResult && (
              <div className="space-y-2" data-testid="combo-result">
                <div className={`flex items-center justify-between p-3 rounded-md border ${comboResult.railtel_status === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-700">Railtel leg</p>
                    <p className="text-xs text-slate-500 truncate">{comboResult.railtel_message || (comboResult.railtel_status === 'success' ? 'OK' : 'Failed')}</p>
                  </div>
                  {comboResult.railtel_status === 'success' ? (
                    <Badge className="bg-emerald-600 text-white">Success</Badge>
                  ) : (
                    <Button size="sm" onClick={() => handleComboRetry('railtel')} disabled={comboRetryBusy.railtel} className="bg-amber-600 hover:bg-amber-700" data-testid="combo-retry-railtel">
                      {comboRetryBusy.railtel ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                      Retry
                    </Button>
                  )}
                </div>
                <div className={`flex items-center justify-between p-3 rounded-md border ${comboResult.iptv_status === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-700">IPTV leg</p>
                    <p className="text-xs text-slate-500 truncate">{comboResult.iptv_message || (comboResult.iptv_status === 'success' ? 'OK' : 'Failed')}</p>
                  </div>
                  {comboResult.iptv_status === 'success' ? (
                    <Badge className="bg-emerald-600 text-white">Success</Badge>
                  ) : (
                    <Button size="sm" onClick={() => handleComboRetry('iptv')} disabled={comboRetryBusy.iptv} className="bg-amber-600 hover:bg-amber-700" data-testid="combo-retry-iptv">
                      {comboRetryBusy.iptv ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                      Retry
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-500 italic">Wallet was charged once for the combo. Retries do not re-charge.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCombo(null); setComboResult(null); setComboId(''); }}>
              {comboResult ? 'Close' : 'Cancel'}
            </Button>
            {!comboResult && (
              <Button onClick={handleComboRecharge} disabled={comboBusy || !comboId} data-testid="combo-recharge-submit" className="bg-purple-600 hover:bg-purple-700">
                {comboBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <IndianRupee className="h-4 w-4 mr-1" />}
                Recharge with Combo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top-up Dialog */}
      <Dialog open={showTopup} onOpenChange={setShowTopup}>
        <DialogContent>
          <DialogHeader><DialogTitle>Top up Railtel Wallet</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Amount (Rs.) *</Label>
            <Input type="number" min={1} value={topupAmount} onChange={e => setTopupAmount(e.target.value)} />
            <div className="flex flex-wrap gap-2">
              {[500, 1000, 2000, 5000].map(a => (
                <Button key={a} variant="outline" size="sm" onClick={() => setTopupAmount(a)}>{RS(a)}</Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTopup(false)}>Cancel</Button>
            <Button onClick={handleTopup}><IndianRupee className="h-4 w-4 mr-1" /> Pay via Easebuzz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
